import { Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { User } from "../../_models/User";
import { map, mergeMap, tap } from "rxjs/operators";
import { UserRole } from "../../_models/UserRole";
import { FuseNavigationService } from "../../../../@fuse/components/navigation/navigation.service";
import { DisciplinaryOrder } from "app/main/profile/users/_models/DisciplinaryOrder";
import { Management, TraineeInsert } from "app/main/_models/trainee";
import { PrivilegesService } from "app/main/profile/_services/privileges.service";
import { MenuService, ApiMenuItem } from "./menu.service";
import swal from "sweetalert2";

@Injectable({
    providedIn: "root",
})
export class AuthenticationService {
    protected api_url = environment.api_url;
    private currentUserSubject: BehaviorSubject<User>;
    public currentUser: Observable<User>;
    private base = `${environment.api_url}/api/trainee`;

    constructor(
        protected httpClient: HttpClient,
        private _fuseNavigationService: FuseNavigationService,
        private privilegesService: PrivilegesService,
        private menuService: MenuService,
    ) {
        this.currentUserSubject = new BehaviorSubject<User>(
            JSON.parse(localStorage.getItem("currentUser")),
        );
        this.currentUser = this.currentUserSubject.asObservable();

        // Ne pas charger les menus automatiquement ici
        // Ils seront chargés après une connexion réussie dans updateUser
    }

    /**
     * Charge les menus dynamiquement depuis l'API
     */
    private loadDynamicNavigation(): void {
        if (!this.currentUserValue || !localStorage.getItem("token")) {
            this.setFallbackNavigation();
            return;
        }

        // Déterminer le mode de navigation selon le rôle
        const navigationMode = this.getNavigationMode();

        this.menuService.getUserMenus(navigationMode).subscribe({
            next: (menus) => {
                // Les menus sont déjà dans le bon format depuis MenuService
                const filteredMenus = this.filterMenusForUser(menus);
                const mainGroup = {
                    id: "main-group",
                    title: this.getMenuTitle(navigationMode),
                    type: "group",
                    icon: "apps",
                    children: filteredMenus,
                };

                // Enregistrer les menus dynamiques
                try {
                    this._fuseNavigationService.unregister("dynamic");
                } catch (_) {}
                this._fuseNavigationService.register("dynamic", [mainGroup]);
                this._fuseNavigationService.setCurrentNavigation("dynamic");

                // Appliquer les privilèges si nécessaire
                this.applyAdminPrivilegesMask();
            },
            error: (error) => {
                // En cas d'erreur, vérifier si c'est un problème d'authentification
                if (error.status === 401 || error.status === 403) {
                    // Token invalide, déconnecter l'utilisateur
                    this.logout();
                } else {
                    console.warn(
                        "Erreur lors du chargement des menus dynamiques, utilisation du menu de secours:",
                        error,
                    );
                    // En cas d'erreur non-critique, utiliser un menu de base
                    this.setFallbackNavigation();
                }
            },
        });
    }

    /**
     * Définit une navigation de secours en cas d'erreur ou si l'utilisateur n'est pas connecté
     */
    private setFallbackNavigation(): void {
        // Si l'utilisateur n'est pas connecté, ne pas afficher de menu
        if (!this.currentUserValue || !localStorage.getItem("token")) {
            try {
                this._fuseNavigationService.unregister("fallback");
            } catch (_) {}
            this._fuseNavigationService.register("fallback", []);
            this._fuseNavigationService.setCurrentNavigation("fallback");
            return;
        }

        // Menu de base pour utilisateur connecté
        const fallbackMenu = [
            {
                id: "fallback",
                title: "Menu Principal",
                type: "group",
                icon: "apps",
                children: [
                    {
                        id: "actuality",
                        title: "Actualités",
                        type: "item",
                        icon: "home",
                        url: "/profile/actuality",
                    },
                    {
                        id: "profile",
                        title: "Mon Profil",
                        type: "item",
                        icon: "person",
                        url: "/profile/info",
                    },
                ],
            },
        ];

        try {
            this._fuseNavigationService.unregister("fallback");
        } catch (_) {}
        this._fuseNavigationService.register("fallback", fallbackMenu);
        this._fuseNavigationService.setCurrentNavigation("fallback");
    }

    /**
     * Recharge les menus dynamiques (à appeler après connexion ou changement de rôle)
     */
    public refreshNavigation(): void {
        this.loadDynamicNavigation();
    }

    /**
     * Initialise les menus au démarrage de l'application
     */
    public initializeNavigation(): void {
        if (this.currentUserValue && localStorage.getItem("token")) {
            this.loadDynamicNavigation();
        } else {
            this.setFallbackNavigation();
        }
    }

    private updateNavigation(): void {
        // Cette méthode est maintenant obsolète, remplacée par loadDynamicNavigation
        // Gardée pour compatibilité, mais redirige vers la nouvelle méthode
        this.loadDynamicNavigation();
    }

    private applyAdminPrivilegesMask(): void {
        try {
            if (!this.hasRole("admin")) {
                return;
            }
            const userId =
                this.currentUserSubject.value?.publicId ||
                this.currentUserSubject.value?.id;
            if (!userId) {
                return;
            }
            const privileges =
                this.privilegesService.getUserPrivilegesSync(userId);
            const denied = new Set<string>(privileges?.deniedMenuIds || []);
            const nav =
                this._fuseNavigationService.getNavigation("dynamic") || [];
            const hideRec = (nodes: any[]) => {
                nodes.forEach((n) => {
                    if (!n) {
                        return;
                    }
                    if (n.id && denied.has(n.id)) {
                        this._fuseNavigationService.updateNavigationItem(n.id, {
                            hidden: true,
                        });
                    }
                    if (Array.isArray(n.children)) {
                        hideRec(n.children);
                    }
                });
            };
            hideRec(nav);
        } catch (_e) {
            /* noop */
        }
    }

    private filterMenusForUser(menus: any[]): any[] {
        const hasCompanyAssociation = this.userHasCompanyAssociation();
        const companyMenuCode = "COTISATION_SOCIETE";

        const deepFilter = (items: any[]): any[] => {
            if (!Array.isArray(items)) {
                return items;
            }
            return items
                .map((item) => {
                    const cloned = { ...item };
                    if (Array.isArray(cloned.children)) {
                        cloned.children = deepFilter(cloned.children);
                    }
                    return cloned;
                })
                .filter(
                    (item) =>
                        item?.id !== companyMenuCode || hasCompanyAssociation,
                );
        };

        return deepFilter(menus);
    }

    private userHasCompanyAssociation(): boolean {
        const soldeCompany: any = this.currentUserValue?.soldeCompany;
        if (!soldeCompany) {
            return false;
        }
        try {
            return Object.keys(soldeCompany).length > 0;
        } catch (_) {
            return false;
        }
    }

    public get currentUserValue(): User {
        return this.currentUserSubject.value;
    }

    public oldhasRole(role: string): boolean {
        const _roles_trainee_user = this.currentUserValue?.roles?.filter(
            (roleDto: UserRole) =>
                roleDto.role.toLowerCase().includes(role.toLowerCase()),
        );
        const _roles = this.currentUserValue?.roles?.filter(
            (roleDto: UserRole) =>
                roleDto.role.toLowerCase().indexOf(role.toLowerCase()) >= 0,
        );
        if (_roles?.length > 0) {
            return true;
        }
        return false;
    }
    public hasRole(role: string): boolean {
        const formattedRole = role.toUpperCase().startsWith("ROLE_")
            ? role.toUpperCase()
            : `ROLE_${role.toUpperCase()}`;
        const rawUserRoles = this.currentUserValue?.roles?.map(
            (roleDto: UserRole) => (roleDto.role || "").toUpperCase(),
        );
        const userRoles = rawUserRoles?.map((r) =>
            r.startsWith("ROLE_") ? r : `ROLE_${r}`,
        );
        if (!userRoles || userRoles.length === 0) {
            return false;
        }
        if (userRoles.includes(formattedRole)) {
            return true;
        }
        // Compat: "external" doit matcher ROLE_EXTERNAL_SUPERVISOR
        if (formattedRole === "ROLE_EXTERNAL") {
            return userRoles.includes("ROLE_EXTERNAL_SUPERVISOR");
        }
        return false;
    }

    public hasRoleExternalSupervisor(): boolean {
        return (
            this.hasRole("EXTERNAL_SUPERVISOR") || this.hasRole("external")
        );
    }

    public hasRoleTrainee(): boolean {
        const userRoles = this.currentUserValue?.roles?.map(
            (roleDto: UserRole) => roleDto.role.toUpperCase(),
        );

        if (!userRoles || userRoles.length === 0) {
            return false; // No roles available
        }

        // Check if 'ROLE_TRAINEE' is in the list of roles
        return userRoles.includes("ROLE_TRAINEE");
    }

    /**
     * Permettant d'inserer un stagiaire
     * @param insertTrainee
     */
    createManagementTrainee(
        insertTrainee: TraineeInsert,
        type: string,
    ): Observable<any> {
        let headers = new HttpHeaders();
        headers = headers.append("password", insertTrainee.trainee.password);
        headers = headers.append("type", type);

        return this.httpClient.request<any>(
            "post",
            `${this.api_url}/api/trainee/add`,
            {
                headers: headers,
                body: insertTrainee,
            },
        );
    }

    /**
     * Permettant de modifier un stagiaire
     * @param updateTrainee
     */
    updateManagementTrainee(
        updateTrainee: TraineeInsert,
        publicId: string,
    ): Observable<any> {
        let headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "put",
            `${this.api_url}/api/trainee/update/${publicId}`,
            {
                headers: headers,
                body: updateTrainee,
            },
        );
    }

    createStage(
        traineePublicId: string,
        startDate: string,
        endDate: string,
        semesters: Array<{
            startDate: string;
            endDate: string;
            totalMonths: number;
        }>,
        controller: { id?: number; folderNumber?: string; publicId?: string },
        supervisor: { id?: number; folderNumber?: string; publicId?: string } | null,
        quality: any,
        externalSupervisor?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            gsm?: string;
        } | null,
    ): Observable<Management> {
        const payload: any = {
            startDate,
            endDate,
            semesters,
            controller,
            quality,
        };
        if (externalSupervisor) {
            payload.externalSupervisor = externalSupervisor;
            payload.internalSupervisor = null;
        } else {
            payload.internalSupervisor = supervisor;
            payload.externalSupervisor = null;
        }
        return this.httpClient.post<Management>(
            `${this.api_url}/api/trainee/${traineePublicId}/management`,
            payload,
        );
    }

    /**
     * Met à jour les infos de stage d’un Management existant.
     */
    updateStage(
        managementId: string,
        startDate: string,
        endDate: string,
        semesters: Array<{
            startDate: string;
            endDate: string;
            totalMonths: number;
        }>,
        controller?: { id?: number; folderNumber?: string; publicId?: string },
        supervisor?: { id?: number; folderNumber?: string; publicId?: string } | null,
        quality?: any,
        externalSupervisor?: {
            firstName?: string;
            lastName?: string;
            email?: string;
            gsm?: string;
        } | null,
    ): Observable<Management> {
        const payload: any = { startDate, endDate, semesters };
        if (controller) payload.controller = controller;
        if (externalSupervisor) {
            payload.externalSupervisor = externalSupervisor;
            payload.internalSupervisor = null;
        } else if (supervisor) {
            payload.internalSupervisor = supervisor;
            payload.externalSupervisor = null;
        }
        payload.quality = quality;
        return this.httpClient.put<Management>(
            `${this.api_url}/api/trainee/${managementId}/management`,
            payload,
        );
    }

    // management.service.ts
    updateManagement(
        managementId: string,
        updates: Partial<Management>,
    ): Observable<Management> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${localStorage.getItem("token")}`,
        });

        return this.httpClient.patch<Management>(
            `${this.api_url}/api/management/${managementId}`,
            updates,
            { headers },
        );
    }
    /**
     * Validation les information d'utilisateur et modifier
     *
     * @param sign
     * @param publicId
     * @param token
     *
     */
    authentificationSign(
        sign: any,
        publicId: string,
        token: string,
    ): Observable<any> {
        if (sign === null || sign === undefined) {
            throw new Error("Required parameter User was null or undefined.");
        }

        if (publicId === null || publicId === undefined) {
            throw new Error(
                "Required parameter publicId user was null or undefined.",
            );
        }

        const queryParameters = new HttpParams();

        let headers = new HttpHeaders();

        headers = headers.set("Authorization", "Bearer " + token);

        return this.httpClient.request<any>(
            "post",
            `${this.api_url}/api/authentification/sign/${publicId}`,
            {
                body: sign,
                headers: headers,
            },
        );
    }

    /**
     * Permettant de retourner les information d'utilisateur
     *
     * @param cardNumber
     * @param cin
     *
     */
    authentification(
        cardNumber: number,
        cin: number,
        reCaptcha: string,
    ): Observable<any> {
        if (cardNumber === null || cardNumber === undefined) {
            throw new Error(
                "Required parameter cardNumber was null or undefined.",
            );
        }

        if (cin === null || cin === undefined) {
            throw new Error("Required parameter cin was null or undefined.");
        }

        if (reCaptcha === null || reCaptcha === undefined) {
            throw new Error(
                "Required parameter reCaptcha was null or undefined.",
            );
        }

        return this.httpClient.request<any>(
            "post",
            `${this.api_url}/api/authentification/init`,
            {
                body: {
                    cin: cin,
                    cardNumer: cardNumber,
                    reCaptcha: reCaptcha,
                },
            },
        );
    }

    /**
     * Permettant de retourner les informations details de lutilisateur courant
     * Permettant de retourner les informations details de lutilisateur courant
     */
    getUserDetails(): Observable<any> {
        const queryParameters = new HttpParams();

        const headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "get",
            `${this.api_url}/api/user/details`,
            {
                headers: headers,
            },
        );
    }

    /**
     * Permettant de rechercher les lutilisateur par email
     * @param email string
     */
    findUserByEmail(email: string): Observable<any> {
        const queryParameters = new HttpParams();

        const headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "get",
            `${this.api_url}/api/user/searchByEmail/${email}`,
            {
                headers: headers,
            },
        );
    }

    /**
     * Permettant de retourner les informations details de lutilisateur courant
     */
    getUserByCardNumber(cin: string, cardNumber: string): Observable<any> {
        const queryParameters = new HttpParams();

        const headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "get",
            `${this.api_url}/api/user/getByCardNumber/${cin}/${cardNumber}`,
            {
                headers: headers,
            },
        );
    }

    /**
     * Validation les information d'utilisateur et modifier
     *
     * @param email
     * @param password
     *
     */
    authentificationSignIn(signIn: any, type: string): Observable<any> {
        if (signIn === null || signIn === undefined) {
            throw new Error("Required parameter User was null or undefined.");
        }
        const headers = new HttpHeaders();

        return this.httpClient
            .request<any>("post", `${this.api_url}/api/${type}/sign-in`, {
                body: signIn,
                headers: headers,
            })
            .pipe(
                tap(({ token, refreshToken }) => {
                    localStorage.setItem("token", token);
                    localStorage.setItem("refreshToken", refreshToken);
                }),
            );
    }

    updateUser(user: User): void {
        this.currentUserSubject.next(user);
        // Charger les menus dynamiques après une connexion réussie
        if (user && localStorage.getItem("token")) {
            this.loadDynamicNavigation();
        }
    }

    /**
     * Changement de mot de passe obligatoire à la première connexion.
     */
    changePasswordFirstLogin(password: string, confirmPassword: string): Observable<any> {
        const headers = new HttpHeaders();
        return this.httpClient.request<any>(
            "post",
            `${this.api_url}/api/user/first-login/change-password`,
            {
                headers: headers,
                body: { password, confirmPassword },
            },
        );
    }

    /**
     * Affiche une popup bloquante si firstLogin === 1 (stagiaires).
     * Retourne une Promise résolue après succès (ou immédiatement si non applicable).
     */
    promptFirstLoginPasswordChange(user: User): Promise<void> {
        if (!user || Number(user.firstLogin) !== 1) {
            return Promise.resolve();
        }

        const showPrompt = (): Promise<void> => {
            return swal
                .fire({
                    title: "",
                    html: `
                        <div class="first-login-pwd">
                            <div class="first-login-pwd__icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                                    <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm-3 8V6a3 3 0 1 1 6 0v3H9z"/>
                                </svg>
                            </div>
                            <h2 class="first-login-pwd__title">Modification du mot de passe</h2>
                            <p class="first-login-pwd__desc">
                                Pour des raisons de sécurité, veuillez définir un nouveau mot de passe.
                            </p>
                            <label class="first-login-pwd__label" for="swal-password">Nouveau mot de passe</label>
                            <div class="first-login-pwd__field">
                                <input id="swal-password" type="password" class="first-login-pwd__input"
                                    placeholder="Saisissez votre nouveau mot de passe"
                                    autocomplete="new-password" />
                                <button type="button" class="first-login-pwd__toggle" data-target="swal-password" aria-label="Afficher le mot de passe">
                                    <svg class="eye-open" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
                                    <svg class="eye-closed" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style="display:none"><path d="M2 4.27L3.28 3 21 20.72 19.73 22l-2.32-2.32A11.8 11.8 0 0 1 12 19C5 19 2 12 2 12a18.5 18.5 0 0 1 5.18-5.93L2 4.27zM12 7a5 5 0 0 1 5 5c0 .7-.15 1.36-.4 1.97l-1.55-1.55A3 3 0 0 0 12 9c-.4 0-.78.08-1.13.23L9.3 7.66A4.9 4.9 0 0 1 12 7zm-7.6 3.3A16.3 16.3 0 0 0 4.05 12S6.5 17 12 17c1.1 0 2.1-.2 3-.52l-1.7-1.7A5 5 0 0 1 7.9 9.2L4.4 10.3z"/></svg>
                                </button>
                            </div>
                            <label class="first-login-pwd__label" for="swal-confirm">Confirmer le mot de passe</label>
                            <div class="first-login-pwd__field">
                                <input id="swal-confirm" type="password" class="first-login-pwd__input"
                                    placeholder="Confirmez votre nouveau mot de passe"
                                    autocomplete="new-password" />
                                <button type="button" class="first-login-pwd__toggle" data-target="swal-confirm" aria-label="Afficher le mot de passe">
                                    <svg class="eye-open" viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
                                    <svg class="eye-closed" viewBox="0 0 24 24" width="20" height="20" fill="currentColor" style="display:none"><path d="M2 4.27L3.28 3 21 20.72 19.73 22l-2.32-2.32A11.8 11.8 0 0 1 12 19C5 19 2 12 2 12a18.5 18.5 0 0 1 5.18-5.93L2 4.27zM12 7a5 5 0 0 1 5 5c0 .7-.15 1.36-.4 1.97l-1.55-1.55A3 3 0 0 0 12 9c-.4 0-.78.08-1.13.23L9.3 7.66A4.9 4.9 0 0 1 12 7zm-7.6 3.3A16.3 16.3 0 0 0 4.05 12S6.5 17 12 17c1.1 0 2.1-.2 3-.52l-1.7-1.7A5 5 0 0 1 7.9 9.2L4.4 10.3z"/></svg>
                                </button>
                            </div>
                        </div>
                    `,
                    focusConfirm: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    showCancelButton: false,
                    confirmButtonText: "Enregistrer",
                    confirmButtonColor: "#f89400",
                    customClass: {
                        popup: "first-login-pwd-popup",
                        title: "first-login-pwd-title-hidden",
                        htmlContainer: "first-login-pwd-html",
                        confirmButton: "first-login-pwd-confirm",
                        validationMessage: "first-login-pwd-validation",
                    },
                    showLoaderOnConfirm: true,
                    didOpen: () => {
                        const popup = swal.getPopup();
                        popup?.querySelectorAll(".first-login-pwd__toggle").forEach((btn) => {
                            btn.addEventListener("click", () => {
                                const targetId = (btn as HTMLElement).dataset.target;
                                const input = document.getElementById(targetId) as HTMLInputElement;
                                if (!input) {
                                    return;
                                }
                                const show = input.type === "password";
                                input.type = show ? "text" : "password";
                                const openIcon = btn.querySelector(".eye-open") as HTMLElement;
                                const closedIcon = btn.querySelector(".eye-closed") as HTMLElement;
                                if (openIcon && closedIcon) {
                                    openIcon.style.display = show ? "none" : "block";
                                    closedIcon.style.display = show ? "block" : "none";
                                }
                            });
                        });

                        const passwordInput = document.getElementById("swal-password") as HTMLInputElement;
                        const updateRules = () => this.updateFirstLoginPasswordRules(passwordInput?.value || "");
                        passwordInput?.addEventListener("input", updateRules);
                        updateRules();
                        passwordInput?.focus();
                    },
                    preConfirm: () => {
                        const password = (
                            document.getElementById("swal-password") as HTMLInputElement
                        )?.value?.trim();
                        const confirmPassword = (
                            document.getElementById("swal-confirm") as HTMLInputElement
                        )?.value?.trim();

                        if (!password || !confirmPassword) {
                            swal.showValidationMessage("Veuillez remplir les deux champs");
                            return false;
                        }

                        const strengthError = this.getStrongPasswordError(password);
                        if (strengthError) {
                            swal.showValidationMessage(strengthError);
                            return false;
                        }

                        if (password !== confirmPassword) {
                            swal.showValidationMessage(
                                "Les mots de passe ne correspondent pas",
                            );
                            return false;
                        }

                        return this.changePasswordFirstLogin(password, confirmPassword)
                            .toPromise()
                            .then(() => ({ password }))
                            .catch((err) => {
                                const msg =
                                    err?.error?.message ||
                                    "Erreur lors de la modification du mot de passe";
                                swal.showValidationMessage(msg);
                                return false;
                            });
                    },
                })
                .then((result) => {
                    if (result.isConfirmed) {
                        user.firstLogin = 0;
                        localStorage.setItem("currentUser", JSON.stringify(user));
                        this.updateUser(user);
                        return swal
                            .fire({
                                title: "Mot de passe modifié",
                                text: "Votre mot de passe a été mis à jour avec succès.",
                                icon: "success",
                                confirmButtonColor: "#68a45b",
                                confirmButtonText: "OK",
                            })
                            .then(() => undefined);
                    }
                    // Si fermé sans succès, relancer (popup bloquante)
                    return showPrompt();
                });
        };

        return showPrompt();
    }

    /**
     * Règles d'un mot de passe fort : 8+ caractères, majuscule, minuscule, chiffre, caractère spécial.
     */
    private getStrongPasswordError(password: string): string | null {
        if (!password || password.length < 8) {
            return "Le mot de passe doit contenir au moins 8 caractères";
        }
        if (!/[A-Z]/.test(password)) {
            return "Le mot de passe doit contenir au moins une lettre majuscule";
        }
        if (!/[a-z]/.test(password)) {
            return "Le mot de passe doit contenir au moins une lettre minuscule";
        }
        if (!/[0-9]/.test(password)) {
            return "Le mot de passe doit contenir au moins un chiffre";
        }
        if (!/[^A-Za-z0-9]/.test(password)) {
            return "Le mot de passe doit contenir au moins un caractère spécial (!@#$%…)";
        }
        return null;
    }

    private updateFirstLoginPasswordRules(password: string): void {
        const checks: Array<{ id: string; ok: boolean }> = [
            { id: "rule-length", ok: !!password && password.length >= 8 },
            { id: "rule-upper", ok: /[A-Z]/.test(password) },
            { id: "rule-lower", ok: /[a-z]/.test(password) },
            { id: "rule-digit", ok: /[0-9]/.test(password) },
            { id: "rule-special", ok: /[^A-Za-z0-9]/.test(password) },
        ];

        checks.forEach(({ id, ok }) => {
            const el = document.getElementById(id);
            if (!el) {
                return;
            }
            el.classList.toggle("is-ok", ok);
            el.classList.toggle("is-ko", !!password && !ok);
        });
    }

    logout(): void {
        // remove user from local storage to log user out
        localStorage.removeItem("currentUser");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        this.currentUserSubject.next(null);

        // Nettoyer les menus et revenir au menu vide
        this.setFallbackNavigation();
    }

    refreshToken(): Observable<any> {
        const headers = new HttpHeaders();
        return this.httpClient.request<any>(
            "post",
            `${this.api_url}/api/authentification/refreshtoken`,
            {
                body: { refreshToken: localStorage.getItem("refreshToken") },
                headers: headers,
            },
        );
    }

    isAuthorized(): boolean {
        return !!this.currentUserValue;
    }

    validateSignature(token: string): Observable<any> {
        if (token === null || token === undefined) {
            throw new Error("Required parameter User was null or undefined.");
        }
        const queryParameters = new HttpParams();
        const headers = new HttpHeaders();
        return this.httpClient.request<any>(
            "put",
            `${this.api_url}/api/authentification/confirm/ngsign/${token}`,
            {
                headers: headers,
            },
        );
    }

    /**
     * Permettant de retourner les informations d'ordre disciplinaire de lutilisateur
     */
    getDisciplinaryOrderByUser(
        publicId: string,
        type: string,
    ): Observable<any> {
        const headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "get",
            `${this.api_url}/api/${type}/disciplinaryOrder/byId/${publicId}`,
            {
                headers: headers,
            },
        );
    }

    /**
     * Permettant d'ajouter un order disciplinaire à un lutilisateur ou société
     */
    addDisciplinaryOrder(
        order: DisciplinaryOrder,
        publicId: string,
        type: string,
    ): Observable<any> {
        const headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "post",
            `${this.api_url}/api/${type}/disciplinaryOrder/add/${publicId}`,
            {
                headers: headers,
                body: order,
            },
        );
    }

    /**
     * Permettant de modifier un order disciplinaire à un lutilisateur ou société
     */
    modifyDisciplinaryOrder(publicId: string, type: string): Observable<any> {
        const headers = new HttpHeaders();

        return this.httpClient.request<any>(
            "put",
            `${this.api_url}/api/user/disciplinaryOrder/reActive/${type}/${publicId}`,
            {
                headers: headers,
            },
        );
    }

    /**
     * Recherche un utilisateur par son identifiant
     */
    findUserById(id: string | number): Observable<any> {
        const headers = new HttpHeaders();
        return this.httpClient.request<any>(
            "get",
            `${this.api_url}/api/user/searchById/${id}`,
            {
                headers,
            },
        );
    }

    /**
     * Liste des ordres disciplinaires d'un utilisateur
     */
    public getDisciplinaryOrdersList(userId: string): Observable<any[]> {
        const headers = new HttpHeaders();
        return this.httpClient.request<any[]>(
            "get",
            `${this.api_url}/api/user/disciplinaryOrder/list/${userId}`,
            {
                headers,
            },
        );
    }

    public getPreferredNavigation(): "admin" | "user" {
        // Si l'utilisateur a le rôle admin et qu'aucune préférence n'est définie, utiliser 'admin' par défaut
        const preferred = localStorage.getItem("preferredNav");
        if (!preferred && this.hasRole("admin")) {
            return "admin";
        }
        const preferredLower = (preferred || "user").toLowerCase();
        return preferredLower === "admin" ? "admin" : "user";
    }

    public setPreferredNavigation(mode: "admin" | "user"): void {
        localStorage.setItem("preferredNav", mode);
        this.updateNavigation();
    }

    public isAdminMode(): boolean {
        if (!(this.hasRole("admin") && this.hasRole("user"))) {
            return this.hasRole("admin");
        }
        return this.getPreferredNavigation() === "admin";
    }

    /**
     * Détermine le titre du menu principal selon le rôle et le mode sélectionné
     */
    private getMenuTitle(mode: string): string {
        // Si l'utilisateur a le rôle SUPER, c'est toujours le titre super admin
        if (this.hasRole("super")) {
            return "Menu principal super administrateur";
        }

        // Stagiaire
        if (this.hasRole("trainee") || this.hasRoleTrainee()) {
            return "Menu principal stagiaire";
        }

        if (this.hasRoleExternalSupervisor()) {
            return "Menu principal maître de stage";
        }

        // Pour les utilisateurs avec double rôle (admin + user), utiliser le mode sélectionné
        if (this.hasRole("admin") && this.hasRole("user")) {
            return mode === "admin"
                ? "Menu principal administrateur"
                : "Menu principal membre";
        }

        // Pour les rôles uniques
        if (this.hasRole("admin")) {
            return "Menu principal administrateur";
        }

        if (this.hasRole("user")) {
            return "Menu principal membre";
        }

        // Fallback par défaut
        return "Menu principal";
    }

    /**
     * Détermine le mode de navigation selon les rôles de l'utilisateur
     */
    private getNavigationMode(): string {
        // SUPER prioritaire
        if (this.hasRole("super")) {
            return "super";
        }

        // TRAINEE prioritaire
        if (this.hasRole("trainee") || this.hasRoleTrainee()) {
            return "trainee";
        }

        // Maître de stage externe
        if (this.hasRoleExternalSupervisor()) {
            return "external";
        }

        // Double rôle admin + user: respecter la préférence
        if (this.hasRole("admin") && this.hasRole("user")) {
            return this.getPreferredNavigation();
        }

        // Rôle ADMIN
        if (this.hasRole("admin")) {
            return "admin";
        }

        // Rôle USER simple
        if (this.hasRole("user")) {
            return "user";
        }

        // Par défaut
        return "user";
    }
}
